import axios from 'axios';

const LOCAL_API_BASE_URL = 'http://localhost:55570/api';
const isBookingPathDeployment = window.location.pathname.startsWith('/booking');
const SAME_HOST_API_BASE_URL = `${window.location.origin}${isBookingPathDeployment ? '/booking/api' : '/api'}`;
const isLocalHost = ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);

const BASE_URL = process.env.REACT_APP_API_BASE_URL || (isLocalHost ? LOCAL_API_BASE_URL : SAME_HOST_API_BASE_URL);

const api = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

const HOLIDAY_SETTING_KEYS = {
  sunday: 'Booking_HolidaySundayClosed',
  secondSaturday: 'Booking_HolidaySecondSaturdayClosed',
  closedDates: 'Booking_HolidayClosedDates',
  openDates: 'Booking_HolidayOpenDates',
  holidayNames: 'Booking_HolidayNames',
};

function parseBoolSetting(value, fallback = true) {
  if (value === null || value === undefined || String(value).trim() === '') return fallback;
  const raw = String(value).trim().toLowerCase();
  if (raw === 'true' || raw === '1') return true;
  if (raw === 'false' || raw === '0') return false;
  return fallback;
}

function parseDateCsv(value) {
  if (!value) return [];
  return String(value)
    .split(/[,\s;\r\n\t]+/)
    .map((v) => v.trim())
    .filter((v) => /^\d{4}-\d{2}-\d{2}$/.test(v));
}

function parseHolidayNamesJson(value) {
  if (!value) return {};
  try {
    const parsed = JSON.parse(String(value));
    if (!parsed || typeof parsed !== 'object') return {};
    const next = {};
    Object.entries(parsed).forEach(([k, v]) => {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(String(k))) return;
      const name = String(v || '').trim();
      if (!name) return;
      next[k] = name;
    });
    return next;
  } catch {
    return {};
  }
}

function mapLegacySettingsToHolidayPayload(settingsList) {
  const all = Array.isArray(settingsList) ? settingsList : [];
  const get = (key) => {
    const row = all.find((s) => String(s.settingKey || '').toLowerCase() === key.toLowerCase());
    return row?.settingValue;
  };

  return {
    sundayClosed: parseBoolSetting(get(HOLIDAY_SETTING_KEYS.sunday), true),
    secondSaturdayClosed: parseBoolSetting(get(HOLIDAY_SETTING_KEYS.secondSaturday), true),
    closedDates: parseDateCsv(get(HOLIDAY_SETTING_KEYS.closedDates)),
    openDates: parseDateCsv(get(HOLIDAY_SETTING_KEYS.openDates)),
    holidayNames: parseHolidayNamesJson(get(HOLIDAY_SETTING_KEYS.holidayNames)),
  };
}

function isLegacyHolidayEndpointMissing(err) {
  const code = err?.response?.status;
  return code === 404 || code === 405;
}

// ── Attach JWT token to every request ──────────────────────
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ── Handle 401 globally ────────────────────────────────────
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const requestUrl = (err.config?.url || '').toLowerCase();
    const isAuthFlowRequest =
      requestUrl.includes('/auth/send-otp') ||
      requestUrl.includes('/auth/verify-otp') ||
      requestUrl.includes('/auth/microsoft-login') ||
      requestUrl.includes('/auth/trusted-device-login') ||
      requestUrl.includes('/auth/request-account');
    const hasActiveSessionToken = !!localStorage.getItem('token');

    // Keep users on login page for expected auth-flow 401 responses (wrong/expired OTP, etc).
    // Force redirect only for authenticated session expiry on protected API calls.
    if (err.response?.status === 401 && hasActiveSessionToken && !isAuthFlowRequest) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// ════════════════════════════════════════════════════════════
//  AUTH
// ════════════════════════════════════════════════════════════
export const authApi = {
  sendOtp: (email) =>
    api.post('/auth/send-otp', { email }),

  requestAccount: (fullName, email, campusID) =>
    api.post('/auth/request-account', { fullName, email, campusID }),

  microsoftLogin: (idToken) =>
    api.post('/auth/microsoft-login', { idToken }),

  verifyOtp: (email, otpCode, trustDevice, deviceName) =>
    api.post('/auth/verify-otp', { email, otpCode, trustDevice, deviceName }),

  trustedDeviceLogin: (email, deviceToken) =>
    api.post('/auth/trusted-device-login', { email, deviceToken }),

  completeRegistration: (campusID, fullName) =>
    api.post('/auth/complete-registration', { campusID, fullName }),

  logout: () =>
    api.post('/auth/logout'),
};

// ════════════════════════════════════════════════════════════
//  CAMPUS
// ════════════════════════════════════════════════════════════
export const campusApi = {
  getAll: (includeInactive = false) =>
    api.get('/campuses', { params: { includeInactive } }),

  getById: (id) =>
    api.get(`/campuses/${id}`),

  create: (data) =>
    api.post('/campuses', data),

  update: (id, data) =>
    api.put(`/campuses/${id}`, data),

  activate: (id) =>
    api.patch(`/campuses/${id}/activate`),

  deactivate: (id) =>
    api.patch(`/campuses/${id}/deactivate`),
};

// ════════════════════════════════════════════════════════════
//  USERS
// ════════════════════════════════════════════════════════════
export const userApi = {
  getAll: (campusId, roleId) =>
    api.get('/users', { params: { campusId, roleId } }),

  getMe: () =>
    api.get('/users/me'),

  getById: (id) =>
    api.get(`/users/${id}`),

  create: (data) =>
    api.post('/users', data),

  update: (id, data) =>
    api.put(`/users/${id}`, data),

  approve: (id) =>
    api.patch(`/users/${id}/approve`),

  lock: (id, reason) =>
    api.patch(`/users/${id}/lock`, { reason }),

  unlock: (id) =>
    api.patch(`/users/${id}/unlock`),

  getDevices: (id) =>
    api.get(`/users/${id}/devices`),

  revokeDevice: (userId, deviceId) =>
    api.delete(`/users/${userId}/devices/${deviceId}`),

  revokeSessions: (id) =>
    api.post(`/users/${id}/revoke-sessions`),
};

// ════════════════════════════════════════════════════════════
//  EVENT AREAS
// ════════════════════════════════════════════════════════════
export const eventAreaApi = {
  getAll: (campusId, includeInactive = false) =>
    api.get('/event-areas', { params: { campusId, includeInactive } }),

  getById: (id) =>
    api.get(`/event-areas/${id}`),

  getSlots: (id, date) =>
    api.get(`/event-areas/${id}/slots`, { params: { date } }),

  create: (data) =>
    api.post('/event-areas', data),

  update: (id, data) =>
    api.put(`/event-areas/${id}`, data),

  activate: (id) =>
    api.patch(`/event-areas/${id}/activate`),

  deactivate: (id) =>
    api.patch(`/event-areas/${id}/deactivate`),
};

// ════════════════════════════════════════════════════════════
//  ASSETS
// ════════════════════════════════════════════════════════════
export const assetApi = {
  getAll: (campusId, includeInactive = false) =>
    api.get('/assets', { params: { campusId, includeInactive } }),

  getById: (id) =>
    api.get(`/assets/${id}`),

  getAvailability: (id, date, startTime, endTime) =>
    api.get(`/assets/${id}/availability`, { params: { date, startTime, endTime } }),

  create: (data) =>
    api.post('/assets', data),

  update: (id, data) =>
    api.put(`/assets/${id}`, data),

  activate: (id) =>
    api.patch(`/assets/${id}/activate`),

  deactivate: (id) =>
    api.patch(`/assets/${id}/deactivate`),
};

// ════════════════════════════════════════════════════════════
//  BOOKINGS
// ════════════════════════════════════════════════════════════
export const bookingApi = {
  create: (data) =>
    api.post('/bookings', data),

  getById: (id) =>
    api.get(`/bookings/${id}`),

  getMy: (status, from, to) =>
    api.get('/bookings/my', { params: { status, from, to } }),

  getPending: (campusId) =>
    api.get('/bookings/pending', { params: { campusId } }),

  getCalendar: (campusId, from, to) =>
    api.get('/bookings/calendar', { params: { campusId, from, to } }),

  review: (id, action, remarks) =>
    api.patch(`/bookings/${id}/review`, { action, remarks }),

  cancel: (id, cancelReason) =>
    api.patch(`/bookings/${id}/cancel`, { cancelReason }),

  getStats: (campusId) =>
    api.get('/bookings/stats', { params: { campusId } }),
};

// ════════════════════════════════════════════════════════════
//  MANAGEMENT TEAM
// ════════════════════════════════════════════════════════════
export const managementApi = {
  getByCampus: (campusId) =>
    api.get(`/management-team/campus/${campusId}`),

  assign: (campusID, userID) =>
    api.post('/management-team', { campusID, userID }),

  remove: (id) =>
    api.delete(`/management-team/${id}`),
};

// ════════════════════════════════════════════════════════════
//  SETTINGS
// ════════════════════════════════════════════════════════════
export const settingsApi = {
  getAll: () =>
    api.get('/settings'),

  update: (key, settingValue) =>
    api.patch(`/settings/${key}`, { settingValue }),

  getHolidays: async () => {
    try {
      return await api.get('/settings/holidays');
    } catch (err) {
      if (!isLegacyHolidayEndpointMissing(err)) throw err;
      const res = await api.get('/settings');
      const list = res.data?.data || res.data || [];
      return {
        ...res,
        data: {
          success: true,
          data: mapLegacySettingsToHolidayPayload(list),
          message: 'Loaded through legacy settings endpoints.',
        },
      };
    }
  },

  saveHolidays: async (payload) => {
    try {
      return await api.put('/settings/holidays', payload);
    } catch (err) {
      if (!isLegacyHolidayEndpointMissing(err)) throw err;
      const sundayClosed = payload?.sundayClosed !== false;
      const secondSaturdayClosed = payload?.secondSaturdayClosed !== false;
      const closedDates = Array.isArray(payload?.closedDates) ? payload.closedDates : [];
      const openDates = Array.isArray(payload?.openDates) ? payload.openDates : [];
      const holidayNames = payload?.holidayNames && typeof payload.holidayNames === 'object' ? payload.holidayNames : {};

      await Promise.all([
        api.patch(`/settings/${HOLIDAY_SETTING_KEYS.sunday}`, { settingValue: sundayClosed ? 'true' : 'false' }),
        api.patch(`/settings/${HOLIDAY_SETTING_KEYS.secondSaturday}`, { settingValue: secondSaturdayClosed ? 'true' : 'false' }),
        api.patch(`/settings/${HOLIDAY_SETTING_KEYS.closedDates}`, { settingValue: closedDates.join(',') }),
        api.patch(`/settings/${HOLIDAY_SETTING_KEYS.openDates}`, { settingValue: openDates.join(',') }),
        api.patch(`/settings/${HOLIDAY_SETTING_KEYS.holidayNames}`, { settingValue: JSON.stringify(holidayNames) }),
      ]);

      return {
        data: {
          success: true,
          message: 'Holiday settings saved through legacy settings endpoints.',
        },
      };
    }
  },
};

export const emailNotificationApi = {
  getAll: () =>
    api.get('/settings/email-notifications'),

  saveAll: (configs) =>
    api.put('/settings/email-notifications', { configs }),
};

// ════════════════════════════════════════════════════════════
//  QR CODE
// ════════════════════════════════════════════════════════════
export const qrApi = {
  generate: (url) =>
    api.get('/qr-code', { params: { url } }),
};

export default api;
